import React, { useEffect, useState } from "react";
import { useForm, Controller } from 'react-hook-form';
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Button from "../../components/ui/button/Button";
import TextArea from "../../components/form/input/TextArea";
import CurrencySelector from "../../components/form/input/CurrencySelector";
import LanguagesSelector from "../../components/form/input/LanguagesSelector";
import MainLanguageSelector from "../../components/form/input/MainLanguageSelector";
import { currencyOptions, findCurrencyByCode } from '../../lib/countries/countries.utils'

import type { MenuVariables, MenuFormProps } from "../../types/menuTypes";
import type { Language } from "../../types/menuTypes";


const MenuForm: React.FC<MenuFormProps> = ({ onSubmit, menu, restaurantId }) => {
  const [languageSelectorIsOpen, setLanguageSelectorIsOpen] = useState(false);
  const [languages, setLanguages ] = useState<Language[] | undefined>(menu?.languages);
  const [currencySelectorIsOpen, setCurrencySelectorIsOpen] = useState(false);
  const [mainLanguage, setMainLanguage] = useState(menu?.mainLanguage || languages?.[0]?.language)
  const { control, register, handleSubmit, reset, formState: { errors }, setValue } = useForm<MenuVariables>({
    defaultValues: {
      restaurantId: restaurantId || menu?.restaurantId,
      languages: languages,
      mainLanguage: mainLanguage
    }
  });

  useEffect(() => {
    if (menu) {
      reset(menu);
    }
  }, [menu, reset]);

  return (
    <div className="grid grid-cols-1 gap-6">
      <form onSubmit={handleSubmit(onSubmit)}>
        <ComponentCard title={`${menu?.id ? 'Edit' : 'New'} menu`}>
          <div className="space-y-6">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input 
                id="name" 
                {...register("name")}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextArea
                    id="description"
                    rows={6}
                    {...field}
                  />
                )}
              />
            </div>
            <div>
              <Label className={errors.currency ? 'text-error-500 dark:text-error-500' : ''}>Currency</Label>
              <Controller
                name="currency"
                control={control}
                rules={{ required: 'Please, select currency' }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <CurrencySelector
                      id="currency"
                      open={currencySelectorIsOpen}
                      onToggle={() => setCurrencySelectorIsOpen(!currencySelectorIsOpen)}
                      options={currencyOptions()}
                      placeholder="Select menu currency"
                      disabled={false}
                      value={field.value?.code}
                      onChange={(selectedValue) => {
                        field.onChange(selectedValue);
                        
                        const currencyObject = findCurrencyByCode(selectedValue);
                        if (currencyObject) {
                          setValue('currency', currencyObject, {
                            shouldValidate: true,
                            shouldDirty: true
                          });
                        }
                      }}
                    />
                    {error && <span className='text-sm text-error-500 mt-2 block'>{error.message}</span>}
                  </>
                )}
              />
            </div>
            <div>
              <Label className={errors.languages ? 'text-error-500 dark:text-error-500' : ''}>Language</Label>
              <Controller
                name="languages"
                control={control}
                rules={{
                  required: true
                }}
                render={({ field }) => (
                  <LanguagesSelector
                    id={"languages"}
                    open={languageSelectorIsOpen}
                    onToggle={() => setLanguageSelectorIsOpen(!languageSelectorIsOpen)}
                    {...field}
                    onChange={(e) => {
                      field.onChange(e)
                      setLanguages(e)
                    }}
                    selectedLanguages={languages}
                  />
                )}
              />
              {errors.languages && 
                <span className="mt-2 text-sm text-error-500">
                  Select language or languages.
                </span>
              }
            </div>
            {(languages && languages.length > 0) && <div>
                <Label>Please select language witch will be first in QR:</Label>
                <Controller
                  name="mainLanguage"
                  control={control}
                  rules={{ required: 'Please, select main language' }}
                  render={({ field, fieldState: { error }}) => (
                    <>
                      <MainLanguageSelector
                        id={"mainLanguage"}
                        mainLanguage={mainLanguage}
                        {...field}
                        onChange={(e) => {
                          field.onChange(e)
                          setMainLanguage(e)
                        }}
                        languages={languages}
                      />
                      {error && <span className='text-sm text-error-500'>{error.message}</span>}
                    </>
                  )}
                />
              </div>
            }
            <Button 
              size="md" 
              variant="primary"
            >
              {menu ? "Update" : "Save"}
            </Button>
          </div>
        </ComponentCard>
      </form>
    </div>
  )
}

export default MenuForm;
